/**
 * gbi-entrance — IntersectionObserver-driven entrance choreography.
 *
 * Mark elements with [data-enter]. Optional attrs:
 *   data-enter-from = up | down | left | right | scale | overshoot   (default: up)
 *   data-enter-delay = milliseconds (default: 0)
 *   data-enter-duration = milliseconds (default: 700)
 *   data-enter-distance = pixels (default: 20)
 *
 * Elements above the fold animate immediately on load (no IO needed).
 * Elements below the fold animate when scrolled into view.
 * prefers-reduced-motion: no transform, no transition, just set opacity 1.
 */
(function () {
  'use strict';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const FROM_TRANSFORMS = {
    up: (d) => `translateY(${d}px)`,
    down: (d) => `translateY(-${d}px)`,
    left: (d) => `translateX(${d}px)`,
    right: (d) => `translateX(-${d}px)`,
    scale: () => 'scale(0.95)',
    overshoot: (d) => `translateY(${d}px) scale(0.95)`,
  };

  const EASINGS = {
    up: 'cubic-bezier(0.16, 1, 0.3, 1)',
    down: 'cubic-bezier(0.16, 1, 0.3, 1)',
    left: 'cubic-bezier(0.16, 1, 0.3, 1)',
    right: 'cubic-bezier(0.16, 1, 0.3, 1)',
    scale: 'cubic-bezier(0.16, 1, 0.3, 1)',
    overshoot: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
  };

  function prep(el) {
    if (reduceMotion) {
      el.style.opacity = '1';
      el.style.transform = 'none';
      el.setAttribute('data-entered', '');
      return;
    }
    const from = el.getAttribute('data-enter-from') || 'up';
    const distance = parseFloat(el.getAttribute('data-enter-distance')) || 20;
    const duration = parseFloat(el.getAttribute('data-enter-duration')) || 700;
    const delay = parseFloat(el.getAttribute('data-enter-delay')) || 0;
    const easing = EASINGS[from] || EASINGS.up;
    const transform = (FROM_TRANSFORMS[from] || FROM_TRANSFORMS.up)(distance);

    el.style.opacity = '0';
    el.style.transform = transform;
    el.style.transition = `opacity ${duration}ms ${easing} ${delay}ms, transform ${duration}ms ${easing} ${delay}ms`;
    el.style.willChange = 'opacity, transform';
  }

  function reveal(el) {
    if (reduceMotion) return;
    requestAnimationFrame(() => {
      el.style.opacity = '1';
      el.style.transform = 'none';
      el.setAttribute('data-entered', '');
      const total = (parseFloat(el.getAttribute('data-enter-delay')) || 0)
                  + (parseFloat(el.getAttribute('data-enter-duration')) || 700);
      setTimeout(() => { el.style.willChange = ''; }, total + 60);
    });
  }

  function init(root) {
    const elements = (root || document).querySelectorAll('[data-enter]:not([data-entered])');
    if (!elements.length) return;

    elements.forEach(prep);

    if (reduceMotion) return;

    const fold = window.innerHeight;
    const deferred = [];
    elements.forEach((el) => {
      const rect = el.getBoundingClientRect();
      if (rect.top < fold) {
        // Above the fold — reveal on next frame so initial paint shows hidden state briefly
        reveal(el);
      } else {
        deferred.push(el);
      }
    });

    if (deferred.length === 0) return;
    if (!('IntersectionObserver' in window)) {
      deferred.forEach(reveal);
      return;
    }
    const io = new IntersectionObserver((entries, obs) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        reveal(entry.target);
        obs.unobserve(entry.target);
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -40px 0px' });

    deferred.forEach((el) => io.observe(el));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (e) => init(e.target));

  window.GBI = window.GBI || {};
  window.GBI.entrance = { init };
})();
